"use client";

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";

/**
 * Bandeau « L'app Guydeez » du footer — rôles 1 et 2 UNIQUEMENT.
 *
 * Présenté comme une app mobile (badges Android / iOS), mais ce n'est pas une
 * app de store : c'est le site lui-même, posé sur l'écran d'accueil (manifest.ts
 * + appleWebApp dans app/layout.tsx). Les badges ne renvoient donc vers aucun
 * store.
 *
 *  - Dès que le navigateur propose l'installation (Chrome / Edge, sur Android comme
 *    sur ordinateur : l'icône « Installer Guydeez » de la barre d'adresse), un clic
 *    sur un badge ouvre directement cette même fenêtre (`beforeinstallprompt`).
 *  - Sur un iPhone ou un iPad : Apple n'offre AUCUN moyen d'installer depuis une
 *    page ; une ligne indique le seul geste possible (Partager → écran d'accueil).
 *  - Sans proposition du navigateur (app déjà installée, Firefox, Safari sur Mac) :
 *    sur Android une ligne indique le menu, ailleurs elle invite à ouvrir le site
 *    sur le téléphone.
 *
 * Absent quand le site tourne déjà en application : lancé depuis l'écran
 * d'accueil, ou dans la WebView de l'app mobile (pont `__GUYDEEZ_APP__`).
 */

type Device = "ios" | "android" | "desktop";
type Store = "android" | "ios";

// Rôles 1 (Super Admin) et 2 (Admin). Le rôle arrive à plat ou sous roleDetails
// selon la réponse qui a rempli `auth.user`. Pendant une impersonation, `auth.user`
// est le compte simulé : le bandeau disparaît avec son rôle.
const ALLOWED_ROLES = new Set([1, 2]);

// Chrome n'émet `beforeinstallprompt` qu'une fois par page, et parfois avant que le
// rôle soit connu (il ne l'est qu'après la vérification du jeton). L'événement est
// donc gardé dès le chargement du module, pour tout le monde, SANS preventDefault :
// rien ne change pour les visiteurs, on garde seulement de quoi rouvrir la fenêtre.
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: InstallPromptEvent | null = null;
let installedThisSession = false;
const promptListeners = new Set<() => void>();
const notifyPromptListeners = () =>
  promptListeners.forEach((notify) => notify());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    deferredPrompt = event as InstallPromptEvent;
    notifyPromptListeners();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installedThisSession = true;
    notifyPromptListeners();
  });
}

const detectDevice = (): Device => {
  const ua = navigator.userAgent || "";
  // iPadOS se présente comme un Mac : on le reconnaît à son écran tactile.
  if (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

const isRunningAsApp = () =>
  Boolean(window.matchMedia?.("(display-mode: standalone)").matches) ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true ||
  Boolean((window as Window & { __GUYDEEZ_APP__?: unknown }).__GUYDEEZ_APP__);

const AndroidGlyph = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#3DDC84"
      d="M17.6 9.48l1.84-3.18a.38.38 0 0 0-.66-.38l-1.87 3.23A11.4 11.4 0 0 0 12 8.15c-1.8 0-3.5.37-5.0 1.0L5.22 5.92a.38.38 0 0 0-.66.38L6.4 9.48A10.8 10.8 0 0 0 1 18h22a10.8 10.8 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"
    />
  </svg>
);

const IphoneGlyph = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="6" y="1.5" width="12" height="21" rx="3.2" fill="white" />
    <rect x="10" y="3.4" width="4" height="1.5" rx=".75" fill="#0F1A14" />
  </svg>
);

const AppInstallBanner: React.FC = () => {
  const { t } = useTranslation("guydeez");
  const user = useAppSelector((state) => state.auth.user);
  const allowed = ALLOWED_ROLES.has(
    Number(user?.role ?? user?.roleDetails?.id),
  );

  // Tout se lit dans le navigateur : le rendu serveur n'affiche jamais rien.
  const [device, setDevice] = useState<Device | null>(null);
  const [asApp, setAsApp] = useState(true);
  const [installed, setInstalled] = useState(false);
  const [host, setHost] = useState("");
  const [hintFor, setHintFor] = useState<Store | null>(null);
  const badgesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDevice(detectDevice());
    setAsApp(isRunningAsApp());
    setHost(window.location.host);
    const sync = () => setInstalled(installedThisSession);
    sync();
    promptListeners.add(sync);
    return () => {
      promptListeners.delete(sync);
    };
  }, []);

  // L'indication se ferme au premier clic ailleurs.
  useEffect(() => {
    if (!hintFor) return undefined;
    const close = (event: PointerEvent) => {
      if (!badgesRef.current?.contains(event.target as Node)) setHintFor(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [hintFor]);

  if (!allowed || !device || asApp || installed) return null;

  const onBadge = async (store: Store) => {
    const promptEvent = deferredPrompt;
    // Même fenêtre que l'icône d'installation de la barre d'adresse, quel que soit le
    // badge : c'est le navigateur qui installe, pas un store.
    if (promptEvent && device !== "ios") {
      // Une fenêtre d'installation ne sert qu'une fois ; Chrome en réémettra une.
      deferredPrompt = null;
      setHintFor(null);
      try {
        await promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch {
        setHintFor(store);
      }
      return;
    }
    setHintFor((current) => (current === store ? null : store));
  };

  const hintText = (store: Store) => {
    if (store === "android" && device === "android")
      return t("app_banner_android_hint");
    if (store === "ios" && device === "ios") return t("app_banner_ios_hint");
    return t("app_banner_open_on_phone", { host });
  };

  const badges: { store: Store; name: string; glyph: React.ReactNode }[] = [
    { store: "android", name: "Android", glyph: <AndroidGlyph /> },
    { store: "ios", name: "iOS", glyph: <IphoneGlyph /> },
  ];

  return (
    // Le conteneur porte l'espacement : rien ne reste dans le footer quand le
    // bandeau n'est pas rendu.
    <div className="container mt-6 mb-10 lg:mt-0 lg:mb-16">
      <section
        aria-label={t("app_banner_title") as string}
        className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
        style={{
          position: "relative",
          overflow: "visible",
          borderRadius: "var(--g-r-lg)",
          padding: "clamp(20px, 3vw, 32px) clamp(20px, 4vw, 40px)",
          background:
            "linear-gradient(120deg, var(--g-green-ink) 0%, var(--g-green-ada) 100%)",
          color: "white",
          fontFamily: "var(--g-font-sans)",
          boxShadow: "var(--g-shadow-md)",
        }}
      >
        <div className="flex items-center gap-4" style={{ minWidth: 0 }}>
          {/* L'icône réelle : celle qui apparaîtra sur l'écran d'accueil. */}
          <span
            style={{
              flexShrink: 0,
              width: 72,
              height: 72,
              borderRadius: 18,
              background: "white",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 10px 24px rgba(0,0,0,.22)",
            }}
          >
            <img
              src="/icons/apple-touch-icon.png"
              alt=""
              width={58}
              height={58}
            />
          </span>
          <div style={{ minWidth: 0 }}>
            <h2
              style={{
                fontFamily: "var(--g-font-display)",
                fontSize: "clamp(20px, 2.2vw, 24px)",
                fontWeight: 800,
                lineHeight: 1.15,
                margin: 0,
              }}
            >
              {t("app_banner_title")}
            </h2>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 14,
                lineHeight: 1.45,
                color: "rgba(255,255,255,.85)",
              }}
            >
              {t("app_banner_subtitle")}
            </p>
          </div>
        </div>

        {/* Deux colonnes égales sur téléphone, comme les badges de store ; largeur
            fixe à partir du format tablette. */}
        <div
          ref={badgesRef}
          className="grid grid-cols-2 gap-3 md:flex"
          style={{ flexShrink: 0 }}
        >
          {badges.map((badge, index) => (
            <div
              key={badge.store}
              className="md:w-[168px]"
              style={{ position: "relative", minWidth: 0 }}
            >
              <button
                type="button"
                onClick={() => onBadge(badge.store)}
                aria-expanded={hintFor === badge.store}
                aria-label={`${t("app_banner_download_for")} ${badge.name}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  height: 54,
                  padding: "0 12px",
                  borderRadius: 12,
                  background: "#0F1A14",
                  border: "1px solid rgba(255,255,255,.28)",
                  color: "white",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
              >
                {badge.glyph}
                <span
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.1,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      opacity: 0.8,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("app_banner_download_for")}
                  </span>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {badge.name}
                  </span>
                </span>
              </button>

              {hintFor === badge.store && (
                <div
                  role="status"
                  style={{
                    position: "absolute",
                    // Ancrée côté extérieur : la bulle du premier badge part vers
                    // la droite, celle du second vers la gauche, sans sortir de
                    // l'écran sur téléphone.
                    ...(index === 0 ? { left: 0 } : { right: 0 }),
                    bottom: "calc(100% + 10px)",
                    zIndex: 30,
                    width: "max-content",
                    maxWidth: "min(280px, calc(100vw - 48px))",
                    background: "white",
                    color: "var(--g-ink-900)",
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "9px 12px",
                    borderRadius: 10,
                    boxShadow: "var(--g-shadow-lg)",
                  }}
                >
                  {hintText(badge.store)}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AppInstallBanner;
